import React, { PropsWithChildren, ReactNode, useCallback, useContext, useState } from 'react';
import { Button, Icon, Modal } from 'semantic-ui-react';
import { useTranslation2 } from '../Contexts/TranslationContext2';
import { BPButton } from '@/components/bp/bpbutton';

type DialogAction = {
  key?: string | number;
  content: ReactNode;
  onClick?: () => void;
  primary?: boolean;
  negative?: boolean;
};

export type DialogProps = {
  header?: string | ReactNode;
  content?: ReactNode;
  // footer can be a list of actions or a raw node
  footer?: DialogAction[] | ReactNode;
  size?: 'mini' | 'tiny' | 'small' | 'large' | 'fullscreen' | 'huge' | 'medium';
  dimmer?: 'blurring' | 'inverted' | boolean;
  closeOnDimmerClick?: boolean;
  style?: React.CSSProperties;
};

export type UseDialogReturn = {
  show: (props?: DialogProps) => void;
  hide: () => void;
  Dialog: React.FC<DialogProps>;
  isOpen: boolean;
};

/**
 * useDialog - simple hook to show a Semantic UI Modal dialog from anywhere in a component.
 *
 * Usage:
 * const { show, hide, Dialog, isOpen } = useDialog();
 *
 * // Render Dialog somewhere inside your component tree (typically near root of component)
 * <Dialog />
 *
 * // To open the dialog imperatively
 * show({ header: 'Title', content: <div>Body</div>, footer: <BPButton onClick={hide}>Close</BPButton> });
 */
export default function useDialog(): UseDialogReturn {
  const [open, setOpen] = useState(false);
  const [props, setProps] = useState<DialogProps>({});
  const { translations } = useTranslation2();

  const show = useCallback((p?: DialogProps) => {
    setProps((prev) => ({ ...prev, ...(p || {}) }));
    setOpen(true);
  }, []);

  const hide = useCallback(() => {
    setOpen(false);
  }, []);

  const Dialog: React.FC<DialogProps> = (overrideProps = {}) => {
    const merged = { ...props, ...(overrideProps || {}) } as DialogProps;

    const renderFooter = () => {
      if (!merged.footer) {
        const closeLabel = translations?.common?.close ?? 'Close';
        return <BPButton onClick={hide}>{closeLabel}</BPButton>;
      }

      if (Array.isArray(merged.footer)) {
        return (
          <>
            {merged.footer.map((a, i) => (
              <BPButton
                key={(a && (a.key ?? i)) as any}
                primary={a.primary}
                negative={a.negative}
                onClick={() => {
                  a.onClick && a.onClick();
                }}
              >
                {a.content}
              </BPButton>
            ))}
          </>
        );
      }

      return merged.footer as ReactNode;
    };

    return (
      <Modal
        open={open}
        onClose={hide}
        size={merged.size as any}
        closeOnDimmerClick={merged.closeOnDimmerClick ?? true}
        dimmer={merged.dimmer}
        style={merged.style}>
          
        {merged.header ? (<Modal.Header>{merged.header}</Modal.Header>) : null}
        {merged.content ?? null}
        {merged.footer && <Modal.Actions>{renderFooter()}</Modal.Actions>}

      </Modal>
    )
    return (
      <Modal
        open={open}
        onClose={hide}
        size={merged.size as any}
        closeOnDimmerClick={merged.closeOnDimmerClick ?? true}
        dimmer={merged.dimmer}
        style={merged.style}
      >
        {merged.header ? (
          typeof merged.header === 'string' ? (
            <Modal.Header>{merged.header}</Modal.Header>
          ) : (
            <Modal.Header>{merged.header}</Modal.Header>
          )
        ) : null}
        {merged.content ? (
          <Modal.Content>{merged.content}</Modal.Content>
        ) : null}
        <Modal.Actions>{renderFooter()}</Modal.Actions>
      </Modal>
    );
  };

  return {
    show,
    hide,
    Dialog,
    isOpen: open,
  };
}
